import { type ObjectKind, type World, interactionTarget, specOf } from '@tama/game-core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DevPanel } from '../components/DevPanel';
import { DirectionPad } from '../components/DirectionPad';
import { HeaderMenu } from '../components/HeaderMenu';
import { WorldStage } from '../components/WorldStage';
import { Toast } from '../components/Toast';
import { TopBar } from '../components/TopBar';
import { CLOCK_INTERVAL_MS, DEV_MODE_ENABLED } from '../config';
import {
  type DevState,
  type DevTool,
  applyDevPointer,
  createDevState,
  isValidTarget,
} from '../game/devWorld';
import { formatAge } from '../game/format';
import { useDeletePet, usePet, useResetWorld, useSaveWorld, useWorld } from '../game/queries';
import { useAutoHatch } from '../game/useAutoHatch';
import { useNow } from '../game/useNow';
import { directionForKey, useActor, useHeldDirection } from '../game/useWorldControls';
import type { CatAnim } from '../scene/walker';
import { TvButton, useBackHandler, useTvNavigation } from '../tv';
import { BootErrorScreen, BootScreen } from './BootScreen';

/**
 * The game: a pet, the grid it lives on, and — in the browser — the editor for
 * changing that grid.
 *
 * The world is server-authoritative and the pet's position is not: a step has
 * nothing to validate remotely now that nothing can come to harm, so walking is
 * instant and only *edits* make a round trip.
 */
export interface GameScreenProps {
  /** The Supabase user id. Every cache key is scoped to it. */
  userId: string;
}

export function GameScreen({ userId }: GameScreenProps) {
  const { data: serverPet, isLoading, isError, error, refetch } = usePet(userId);
  // No naming screen: the server saying "no pet" is what starts a new one.
  const hatch = useAutoHatch(userId, serverPet);
  const world = useWorld(userId);
  const deletePet = useDeletePet(userId);
  const saveWorld = useSaveWorld(userId);
  const resetWorld = useResetWorld(userId);
  const { focusFirst } = useTvNavigation();
  const now = useNow(CLOCK_INTERVAL_MS);

  const [toast, setToast] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<'release' | 'reset' | 'discard' | null>(null);
  const [reaction, setReaction] = useState<{ key: number; anim: CatAnim } | null>(null);

  /**
   * The world being edited, or `null` when simply playing.
   *
   * Dev mode works on a copy. That is what makes Revert free — throw the draft
   * away — and what stops a half-finished room being what the pet walks around
   * in while it is being drawn.
   */
  const [draft, setDraft] = useState<World | null>(null);
  const [dev, setDev] = useState<DevState>(createDevState);

  const savedWorld = world.data as World | undefined;
  const activeWorld = draft ?? savedWorld;
  const editing = draft !== null;

  const { actor, step } = useActor(activeWorld);
  const { press, release } = useHeldDirection(step, Boolean(actor));

  // Stable, because Toast's dismiss timer keys off it.
  const dismissToast = useCallback(() => setToast(null), []);

  const stageRef = useRef<HTMLDivElement>(null);
  // Keyed on the world appearing rather than on mount: the screen shows the
  // boot spinner first, so at mount there is no frame to focus and a remote
  // would arrive with its arrows pointing at nothing.
  const ready = Boolean(serverPet && activeWorld && actor);
  useEffect(() => {
    if (ready) stageRef.current?.focus();
  }, [ready]);

  // With the arrows walking the pet, the remote needs a way out of the stage;
  // Back is the one button a television is guaranteed to have spare.
  useBackHandler(
    useCallback(() => {
      focusFirst();
    }, [focusFirst]),
  );

  useEffect(() => {
    if (isError && error) setToast(error.message);
  }, [isError, error]);
  useEffect(() => {
    if (world.isError && world.error) setToast(world.error.message);
  }, [world.isError, world.error]);

  /** Looking at whatever is in front of the pet — the seed of loot and quests. */
  const interact = useCallback(() => {
    if (!activeWorld || !actor) return;
    const target = interactionTarget(activeWorld, actor);
    if (!target) {
      setToast('Nothing there.');
      setReaction((previous) => ({ key: (previous?.key ?? 0) + 1, anim: 'sit' }));
      return;
    }
    setToast(specOf(target).examine);
    setReaction((previous) => ({
      key: (previous?.key ?? 0) + 1,
      // The pose is the reply: a bed gets slept in, a rug gets sat on.
      anim: target.kind === 'bed' ? 'sleep' : target.kind === 'rug' ? 'sit' : 'hop',
    }));
  }, [activeWorld, actor]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const direction = directionForKey(event.key);
      if (direction) {
        // Claimed even on an auto-repeat, so the TV navigation layer — which
        // skips events that were already handled — never steals an arrow while
        // the pet is walking.
        event.preventDefault();
        if (!event.repeat) press(direction);
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        interact();
      }
    },
    [press, interact],
  );

  const onKeyUp = useCallback(
    (event: React.KeyboardEvent) => {
      const direction = directionForKey(event.key);
      if (direction) release(direction);
    },
    [release],
  );

  // ------------------------------------------------------------- dev mode

  const dirty = useMemo(
    // A 16×12 map is small enough that comparing the serialised form is cheaper
    // than maintaining a change flag that could get out of step with the truth.
    () => Boolean(draft && savedWorld && JSON.stringify(draft) !== JSON.stringify(savedWorld)),
    [draft, savedWorld],
  );

  const onTilePointer = useCallback(
    (tile: Parameters<typeof isValidTarget>[2], phase: 'down' | 'move' | 'up') => {
      setDraft((current) => {
        if (!current) return current;
        const result = applyDevPointer(current, devRef.current, tile, phase);
        setDev(result.dev);
        return result.world;
      });
    },
    [],
  );

  // The pointer handler runs from the render loop and must see the tool that is
  // selected *now*, not the one captured when it was created.
  const devRef = useRef(dev);
  devRef.current = dev;
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const stageEditing = useMemo(
    () =>
      editing
        ? {
            selectedObjectId: dev.selectedId,
            isValidTarget: (tile: { x: number; y: number }) => {
              const current = draftRef.current;
              return current ? isValidTarget(current, devRef.current, tile) : false;
            },
            onTilePointer,
          }
        : null,
    [editing, dev.selectedId, onTilePointer],
  );

  const closeEditor = useCallback(() => {
    if (dirty) {
      setConfirming('discard');
      return;
    }
    setDraft(null);
  }, [dirty]);

  const onSave = useCallback(async () => {
    if (!draft) return;
    try {
      // The server normalises what it stores, so the draft becomes whatever came
      // back rather than whatever was sent.
      setDraft(await saveWorld.mutateAsync(draft));
      setToast('World saved.');
    } catch (cause) {
      setToast(cause instanceof Error ? cause.message : String(cause));
    }
  }, [draft, saveWorld]);

  const onReset = useCallback(async () => {
    setConfirming(null);
    try {
      setDraft(await resetWorld.mutateAsync());
      setToast('Map reset.');
    } catch (cause) {
      setToast(cause instanceof Error ? cause.message : String(cause));
    }
  }, [resetWorld]);

  const onRelease = useCallback(async () => {
    setConfirming(null);
    try {
      // The cache goes to `null`, which is exactly the state `useAutoHatch`
      // watches for — so a successor is on its way before this returns.
      await deletePet.mutateAsync();
    } catch (cause) {
      setToast(cause instanceof Error ? cause.message : String(cause));
    }
  }, [deletePet]);

  // ------------------------------------------------------------- rendering

  if (isLoading || world.isLoading) return <BootScreen />;

  if (isError && !serverPet) {
    return (
      <BootErrorScreen
        title="Could not reach your pet"
        message={error?.message ?? 'Unknown error'}
        onRetry={() => void refetch()}
      />
    );
  }

  if (hatch.isError) {
    return (
      <BootErrorScreen
        title="Could not hatch a pet"
        message={hatch.error.message}
        onRetry={hatch.retry}
      />
    );
  }

  if (world.isError && !savedWorld) {
    return (
      <BootErrorScreen
        title="Could not load your home"
        message={world.error?.message ?? 'Unknown error'}
        onRetry={() => void world.refetch()}
      />
    );
  }

  // Between "there is no pet" and the new one arriving.
  if (!serverPet || !activeWorld || !actor) return <BootScreen />;

  const ageMs = Math.max(0, now + (serverPet.serverNow - Date.now()) - serverPet.bornAt);

  return (
    // The editor turns the phone layout off — see the phone section of app.css.
    // It needs the whole map on screen and a panel of controls beside it, which
    // is the opposite of what an immersive, camera-following view gives it.
    <div className={editing ? 'screen screen--editing' : 'screen'}>
      <TopBar
        title={serverPet.name}
        titleTestId="pet-name"
        stage={serverPet.stage}
        age={`${formatAge(ageMs)} old`}
      >
        <HeaderMenu>
          {DEV_MODE_ENABLED && !editing && (
            <TvButton data-testid="dev-open" onClick={() => savedWorld && setDraft(savedWorld)}>
              Edit world
            </TvButton>
          )}
          <TvButton
            variant="danger"
            data-testid="delete-pet"
            aria-disabled={deletePet.isPending}
            onClick={() => setConfirming('release')}
          >
            Release
          </TvButton>
        </HeaderMenu>
      </TopBar>

      <div className={editing ? 'world-view world-view--editing' : 'world-view'}>
        <section className="world-view__stage-area">
          {/*
            The focusable is this wrapper, not the canvas: the arrows have to be
            claimed before the D-pad navigation layer sees them, and a DOM node
            is the only thing that can hold focus.
          */}
          <div
            ref={stageRef}
            className="world-frame"
            data-focusable
            data-testid="world-frame"
            /* The pet lives on a canvas, so this is the only way anything
               outside the renderer — a test, a bug report — can say where it is
               standing. */
            data-actor={`${actor.x},${actor.y},${actor.facing}`}
            tabIndex={0}
            role="application"
            aria-label={`${activeWorld.name} — arrow keys to walk, Enter to look`}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            onBlur={() => release()}
          >
            <WorldStage
              world={activeWorld}
              actor={actor}
              stage={serverPet.stage}
              reaction={reaction}
              editing={stageEditing}
            />
          </div>

          <DirectionPad onPress={press} onRelease={release} onInteract={interact} />
        </section>

        {editing && (
          <DevPanel
            world={activeWorld}
            dev={dev}
            dirty={dirty}
            saving={saveWorld.isPending || resetWorld.isPending}
            onToolChange={(tool: DevTool) => setDev((current) => ({ ...current, tool }))}
            onPlaceKindChange={(kind: ObjectKind) =>
              setDev((current) => ({ ...current, placeKind: kind, tool: 'place' }))
            }
            onSave={() => void onSave()}
            onRevert={() => savedWorld && setDraft(savedWorld)}
            onReset={() => setConfirming('reset')}
            onClose={closeEditor}
          />
        )}
      </div>

      <ConfirmDialog
        open={confirming === 'release'}
        testId="delete-pet-dialog"
        title={`Release ${serverPet.name}?`}
        body="This deletes the pet for good, and a new one arrives immediately in its place. Your home is left exactly as it is."
        cancelLabel="Keep"
        confirmLabel="Release"
        destructive
        onConfirm={() => void onRelease()}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        open={confirming === 'reset'}
        testId="dev-reset-dialog"
        title="Reset the map?"
        body="Every wall you have painted and every piece of furniture you have moved goes back to how the game shipped."
        cancelLabel="Keep mine"
        confirmLabel="Reset"
        destructive
        onConfirm={() => void onReset()}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        open={confirming === 'discard'}
        testId="dev-discard-dialog"
        title="Leave without saving?"
        body="The edits you have made since the last save will be thrown away."
        cancelLabel="Keep editing"
        confirmLabel="Discard"
        destructive
        onConfirm={() => {
          setConfirming(null);
          setDraft(null);
        }}
        onCancel={() => setConfirming(null)}
      />

      <Toast message={toast} onDismiss={dismissToast} />
    </div>
  );
}
